
import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import ToolCard from "./ToolCard";
import { ToolUsageStats } from "@/hooks/useToolUsageStats";
import { db } from "@/integrations/cloudflare/client";

interface AvailableToolsListProps {
  toolUsageStats: ToolUsageStats[];
  isLoading: boolean;
}

const AvailableToolsList: React.FC<AvailableToolsListProps> = ({ 
  toolUsageStats,
  isLoading
}) => {
  const [craftCount, setCraftCount] = useState<number>(0);
  const [sceneCount, setSceneCount] = useState<number>(0);
  const [recipeCount, setRecipeCount] = useState<number>(0);
  const [isCountLoading, setIsCountLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setIsCountLoading(true);
        
        // Get craft ideas count
        const { count: craftIdeaCount, error: craftError } = await db
          .from('craft_ideas')
          .select('*', { count: 'exact', head: true });
        
        if (!craftError) setCraftCount(craftIdeaCount || 0);
        
        // Get scene ideas count
        const { count: sceneIdeaCount, error: sceneError } = await db
          .from('elf_scenes')
          .select('*', { count: 'exact', head: true });
        
        if (!sceneError) setSceneCount(sceneIdeaCount || 0);
        
        // Get recipe ideas count
        const { count: recipeIdeaCount, error: recipeError } = await db
          .from('holiday_recipes')
          .select('*', { count: 'exact', head: true });
        
        if (!recipeError) setRecipeCount(recipeIdeaCount || 0);
      } catch (error) {
        console.error("Error fetching counts:", error);
      } finally {
        setIsCountLoading(false);
      }
    };
    
    fetchCounts();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Available Tools</CardTitle>
        <CardDescription>
          Interactive tools that can be embedded in blog posts
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-5">
          <ToolCard 
            toolId="craftGenerator"
            name="Holiday Elf Craft Generator"
            description="Interactive tool that generates holiday craft ideas based on available supplies."
            configPath="/admin/craft-generator"
            stats={!isLoading ? toolUsageStats.find(t => t.toolName === 'craftGenerator') : undefined}
            craftCount={isCountLoading ? undefined : craftCount}
          />
          
          <ToolCard 
            toolId="sceneGenerator"
            name="Holiday Elf Scene Generator"
            description="Interactive tool that generates creative elf scene ideas for setting up holiday magic."
            configPath="/admin/scene-generator"
            stats={!isLoading ? toolUsageStats.find(t => t.toolName === 'sceneGenerator') : undefined}
            sceneCount={isCountLoading ? undefined : sceneCount}
          />
          
          <ToolCard 
            toolId="recipeGenerator"
            name="Holiday Elf Recipe Generator"
            description="Interactive tool that generates festive holiday recipes for all occasions."
            configPath="/admin/recipe-generator"
            stats={!isLoading ? toolUsageStats.find(t => t.toolName === 'recipeGenerator') : undefined}
            recipeCount={isCountLoading ? undefined : recipeCount}
          />
          
          <ToolCard 
            toolId="elfNameGenerator"
            name="Christmas Elf Name Generator"
            description="Fun interactive tool that creates festive elf names for your holiday celebrations."
            configPath="/admin/elf-name-generator"
            stats={!isLoading ? toolUsageStats.find(t => t.toolName === 'elfNameGenerator') : undefined}
            // Note: No content count needed for Elf Name Generator as it generates names dynamically
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default AvailableToolsList;
